import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  TooltipProvider,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@cargomatic/ui';
import LoadingCircle from 'components/ui/LoadingCircle';
import Pagination from 'components/v2/PaginationV2';
import { showToast } from 'stores/ToastStore';
import { getFileIconClass } from 'helpers/helper_functions';
import { BARCODE } from 'config/documents';
import { useGetDocuments, useGetSignedUrls } from 'utils/DocumentsHooks';

const DOCUMENTS_PER_PAGE = 10;

const getAllForeignKeysForShipment = (shipment) => {
  const invoiceIds =
    shipment.receivables?.map((receivable) => receivable.invoice_id) || [];

  const payoutIds =
    shipment.payables?.map((payable) => payable.payout_id) || [];

  return [shipment._id, ...invoiceIds, ...payoutIds].filter((key) => key);
};

export const ShipmentDocumentList = ({ shipments }) => {
  const [page, setPage] = useState(1);

  const { t } = useTranslation();
  const pageCount = Math.ceil(
    (shipmentDocumentsData?.count || 0) / DOCUMENTS_PER_PAGE,
  );
  const shipmentDocumentForeignKeys = [
    ...new Set(
      shipments
        .map((shipment) => getAllForeignKeysForShipment(shipment))
        .flat()
        .filter((key) => key),
    ),
  ];

  const {
    data: shipmentDocumentsData,
    error: shipmentDocumentsError,
    loading,
  } = useGetDocuments({
    foreignKeys: shipmentDocumentForeignKeys,
    options: {
      limit: DOCUMENTS_PER_PAGE,
      skip: (page - 1) * DOCUMENTS_PER_PAGE,
      order: [{ field: 'createdAt', direction: 'desc' }],
    },
  });
  const { data: signedUrlsData, error: signedUrlsError } = useGetSignedUrls({
    foreignKeys: shipmentDocumentForeignKeys,
  });

  const openSignedUrl = (document) => async (event) => {
    event.preventDefault();

    const signedUrlData = signedUrlsData?.signedUrls.find(
      (data) => data.id === document.id,
    );

    window.open(signedUrlData?.signedUrl, 'new');
  };

  useEffect(() => {
    if (shipmentDocumentsError) {
      showToast(
        t('GET_DOCUMENT_ERROR'),
        t('GET_DOCUMENT_FAILED_WITH_ERROR', { error: shipmentDocumentsError }),
        'error',
      );
    }
  }, [shipmentDocumentsError]);

  useEffect(() => {
    if (signedUrlsError) {
      showToast(
        t('GET_SIGNED_URL_ERROR'),
        t('GET_SIGNED_URL_FAILED_WITH_ERROR', {
          error: signedUrlsError,
        }),
        'error',
      );
    }
  }, [signedUrlsError]);

  const renderShipmentDocuments = (shipment) => {
    const foreignKeys = getAllForeignKeysForShipment(shipment);

    return shipmentDocumentsData?.documents
      .filter(
        (document) =>
          [document.foreignKeys, foreignKeys].reduce((a, b) =>
            a.filter((c) => b.includes(c)),
          ).length,
      )
      .filter((document) => document.type !== BARCODE.value)
      .map((document) => (
        <TableRow key={`shipment-document-${document.key}`}>
          <TableCell className="whitespace-nowrap">
            <div className="flex items-center space-x-2">
              <i
                className={getFileIconClass(
                  document.key.split('.').pop().toUpperCase(),
                )}
              />
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      className="block max-w-[300px] truncate text-left text-blue-600 hover:underline focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                      onClick={openSignedUrl(document)}
                    >
                      {document.name}
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-md break-all">
                    {document.name}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </TableCell>
          <TableCell className="whitespace-nowrap">
            <a
              href={`/shipments/${shipment._id}`}
              target="_blank"
              rel="noreferrer"
              className="text-primary"
            >
              {shipment.shipment_id}
            </a>
          </TableCell>
        </TableRow>
      ));
  };

  if (!shipments.length) {
    return <i>{t('NO_SHIPMENT_ATTACHED_TO_ORDER')}</i>;
  }

  return (
    <>
      {loading ? (
        <LoadingCircle text={t('LOADING')} />
      ) : (
        <div className="overflow-x-auto">
          <Table data-testid="order-documents-table">
            <TableHeader>
              <TableRow>
                <TableHead className="whitespace-nowrap capitalize">
                  {t('DOCUMENT', { count: 1 })}
                </TableHead>
                <TableHead className="whitespace-nowrap capitalize">
                  {t('SHIPMENT', { count: 1 })}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {shipments.map((shipment) => renderShipmentDocuments(shipment))}
            </TableBody>
          </Table>
        </div>
      )}
      <Pagination
        className="border-t-0"
        perPage={DOCUMENTS_PER_PAGE}
        page={page}
        pages={pageCount}
        total={shipmentDocumentsData?.count}
        showCount
        onPageClick={(page) => setPage(page)}
        pageNeighbours={0}
      />
    </>
  );
};
